import { useCallback, useEffect, useState } from "react";
import { fetchProfile, saveProfile, type PortalProfile, type PortalProfileValue } from "./api.js";
import { CardSkeleton, Check, EmptyState, Notice } from "./ui.js";

/**
 * The respondent identity layer, as the person themselves sees it — one canonical profile carried
 * across every form and every year.
 *
 * Grouped by the attribute dictionary's own `category` rather than served as one long column of
 * inputs: a profile that has been through three forms can carry forty attributes, and "bank" and
 * "referee" sitting undivided between "person" and "school" is a wall, not a page.
 */

/**
 * Category titles. The dictionary's keys are terse (`origin`, `referee`) because they are database
 * values; these are what a person reads. An unrecognised category still renders — capitalised —
 * rather than being dropped, so a new attribute group can never make a profile field invisible.
 */
const CATEGORY_TITLES: Record<string, string> = {
  person: "Personal details",
  address: "Address",
  origin: "State of origin",
  education: "Education",
  school: "School",
  employment: "Employment",
  guardian: "Parent or guardian",
  bank: "Bank details",
  referee: "Referee",
};

/** The order groups appear in — identity first, then the things attached to it. */
const CATEGORY_ORDER = Object.keys(CATEGORY_TITLES);

const titleFor = (category: string): string =>
  CATEGORY_TITLES[category] ?? category.charAt(0).toUpperCase() + category.slice(1);

const groupByCategory = (values: PortalProfileValue[]): [string, PortalProfileValue[]][] => {
  const groups = new Map<string, PortalProfileValue[]>();
  for (const value of values) {
    const existing = groups.get(value.category);
    if (existing) existing.push(value);
    else groups.set(value.category, [value]);
  }

  return [...groups.entries()].sort(([left], [right]) => {
    const leftRank = CATEGORY_ORDER.indexOf(left);
    const rightRank = CATEGORY_ORDER.indexOf(right);
    // Anything outside the known list sorts after everything in it, alphabetically among itself.
    if (leftRank === -1 && rightRank === -1) return left.localeCompare(right);
    if (leftRank === -1) return 1;
    if (rightRank === -1) return -1;
    return leftRank - rightRank;
  });
};

export const ProfileScreen = (): JSX.Element => {
  const [profile, setProfile] = useState<PortalProfile | null>(null);
  const [edits, setEdits] = useState<Record<string, unknown>>({});
  const [saved, setSaved] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    void fetchProfile()
      .then((result) => {
        setProfile(result);
        setEdits({});
      })
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "Your details could not be loaded."),
      );
  }, []);

  useEffect(load, [load]);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await saveProfile(edits);
      setSaved(result.changed);
      load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Those changes could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  if (error && !profile) return <Notice tone="error">{error}</Notice>;
  if (!profile) return <CardSkeleton rows={2} />;

  const dirty = Object.keys(edits).length > 0;
  const groups = groupByCategory(profile.profile);

  return (
    <div>
      <h1 className="font-heading text-2xl font-semibold">My profile</h1>
      <p className="sga-muted mt-2 max-w-2xl text-sm">
        These details are carried into every form you apply to, so you never retype them. Correcting
        something here corrects it everywhere from now on — it does not change an application you
        have already submitted.
      </p>

      {profile.profile.length === 0 ? (
        <div className="mt-5">
          <EmptyState title="Nothing saved yet">
            Your details are filled in from your first application. Once you have submitted a form,
            what you told us appears here for you to keep current.
          </EmptyState>
        </div>
      ) : (
        <>
          <div className="mt-5 space-y-4">
            {groups.map(([category, values]) => (
              <section key={category} className="sga-card p-5">
                <h2 className="font-heading text-base font-semibold">{titleFor(category)}</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {values.map((value) => (
                    <div key={value.key}>
                      <label htmlFor={`profile-${value.key}`} className="sga-label">
                        {value.label}
                      </label>
                      <input
                        id={`profile-${value.key}`}
                        className="sga-input mt-1.5"
                        value={String(
                          (edits[value.key] as string | undefined) ??
                            (value.value === null || value.value === undefined ? "" : value.value),
                        )}
                        onChange={(event) =>
                          setEdits((current) => ({ ...current, [value.key]: event.target.value }))
                        }
                      />
                      {value.sourceCycle && (
                        <p className="sga-hint mt-1">From your {value.sourceCycle} application</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* Sticky, because a profile with four groups is taller than a phone screen and the save
              button was previously at the bottom of it — an edit made at the top meant scrolling
              past everything to commit it. */}
          <div
            className="sticky bottom-0 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3"
            style={{
              background: "var(--sga-surface)",
              borderColor: dirty ? "var(--sga-primary)" : "var(--sga-border)",
            }}
          >
            <p className="sga-muted text-sm" aria-live="polite">
              {error ? (
                <span style={{ color: "var(--sga-error)" }}>{error}</span>
              ) : dirty ? (
                `${Object.keys(edits).length} unsaved change${
                  Object.keys(edits).length === 1 ? "" : "s"
                }`
              ) : saved !== null ? (
                <span
                  className="inline-flex items-center gap-1.5"
                  style={{ color: "var(--sga-primary)" }}
                >
                  <Check className="h-4 w-4" />
                  {saved === 0
                    ? "Nothing needed changing."
                    : `${saved} detail${saved === 1 ? "" : "s"} updated.`}
                </span>
              ) : (
                "Everything here is up to date."
              )}
            </p>
            <button
              type="button"
              disabled={!dirty || busy}
              onClick={() => void save()}
              className="sga-btn sga-btn--primary"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
